import { Instagram, Facebook } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { STRINGS } from "@/lib/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";

const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" className={`h-[18px] w-[18px] ${className}`} aria-hidden="true">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const SOCIALS = [
  {
    name: "Instagram",
    handle: "@ricardooliveiragtr",
    href: "https://instagram.com/ricardooliveiragtr",
    testid: "social-instagram",
    Icon: Instagram,
  },
  {
    name: "TikTok",
    handle: "@rickgtr7",
    href: "https://www.tiktok.com/@rickgtr7",
    testid: "social-tiktok",
    Icon: TikTokIcon,
  },
  {
    name: "Facebook",
    handle: "Ricardo Oliveira",
    href: "https://www.facebook.com/ricardogtr",
    testid: "social-facebook",
    Icon: Facebook,
  },
];

export default function Footer() {
  const { lang } = useLanguage();
  const t = STRINGS[lang];
  const links = [
    [t.nav.formats, "#formatos", "footer-link-formatos"],
    [t.nav.levels, "#niveis", "footer-link-niveis"],
    [t.nav.styles, "#estilos", "footer-link-estilos"],
    [t.nav.professor, "#professor", "footer-link-professor"],
    [t.nav.book, "#agendar", "footer-link-agendar"],
    [t.nav.contact, "#contato", "footer-link-contato"],
  ];

  return (
    <footer className="border-t border-white/5 bg-[#04060B]" data-testid="main-footer">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="flex flex-col justify-between gap-12 lg:flex-row lg:items-end">
          <div>
            <div className="flex items-end gap-4">
              <LogoMark className="h-16 w-16" />
              <span className="font-serif text-[88px] font-semibold leading-[0.8] tracking-tight text-blue-50/95 sm:text-[120px]">
                IGL
              </span>
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-blue-400/60">{t.footer.tagline}</p>

            <div className="mt-7 flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map(({ name, handle, href, testid, Icon }) => (
                <a
                  key={testid}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={testid}
                  aria-label={`${name} — ${handle}`}
                  title={`${name} — ${handle}`}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/50 hover:text-blue-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]"
                >
                  <Icon className={name === "TikTok" ? "" : "h-[18px] w-[18px]"} />
                </a>
              ))}
            </div>
          </div>

          <nav className="flex flex-wrap gap-x-10 gap-y-4">
            {links.map(([label, href, testid]) => (
              <button
                key={testid}
                onClick={() => scrollToId(href)}
                data-testid={testid}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 transition-colors duration-300 hover:text-blue-400"
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-14 flex flex-col justify-between gap-4 border-t border-white/5 pt-8 sm:flex-row">
          <p className="text-xs text-slate-600">© {new Date().getFullYear()} {t.footer.rights}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">{t.footer.made}</p>
        </div>
      </div>
    </footer>
  );
}
